import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Calendar,
  Clock,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Activity,
  User,
  TruckIcon,
  XCircle,
  PowerOff
} from "lucide-react";
import { format, parseISO, isToday, isYesterday, startOfWeek, isWithinInterval } from "date-fns";
import { ptBR } from "date-fns/locale";
import { profileClientApi, ProfileHistory } from "@/lib/api";

interface ActivityLogEntry {
  id: string;
  client_code: string;
  client_name: string;
  action_type: string;
  product_code: string;
  product_description: string;
  details: any;
  comment: string | null;
  user_info: any;
  created_at: string;
}

// Catálogo de `action` (chave dentro de comment.details, após parse) e labels
// PT-BR correspondentes. Fonte da verdade: .claude/docs/3_TASK.md. Qualquer
// action fora deste mapa cai no bucket "OTHER" (Outros) em vez de quebrar a UI.
const ACTION_LABELS = {
  add: 'Produto Adicionado',
  edit: 'Produto Editado',
  activate: 'Produto Reativado',
  remove: 'Produto Removido',
  cycle_generation: 'Produto Gerado (Tombamento)',
  create_scheduled_item: 'Item Programado Adicionado',
  update_scheduled_item: 'Item Programado Editado',
  create_scheduled_order: 'Pedido Programado Criado',
  update_scheduled_order: 'Pedido Programado Editado',
  // Pedido complementar acumulado no programado (ScheduledOrderCompositionService)
  accumulate_scheduled_order: 'Pedido Programado Editado',
  // Cancelamento de pedido (OrderService)
  cancel_order: 'Pedido Cancelado',
  confirm_scheduled_order: 'Pedido Programado Confirmado',
  update_confirmed_scheduled_order: 'Pedido Confirmado Editado',
  create_client_adjustment: 'Reajuste Adicionado',
  update_client_adjustment: 'Reajuste Editado',
  delete_client_adjustment: 'Reajuste Removido',
  adjustment_change: 'Reajuste Alterado',
  create_client_adjustment_bulk: 'Reajuste Adicionado (Lote)',
  update_client_adjustment_bulk: 'Reajuste Editado (Lote)',
  editFee: 'Frete Editado',
  create_simulated_shipment: 'Simulado Adicionado',
  update_simulated_shipment: 'Simulado Editado',
  editPaymentPlan: 'Plano de Pagamentos Editado',
  editCreateZendeskTicketOnOrder: 'Chamado no Zendesk ao Criar Pedido Editado',
  // Grupo e endereços do cliente (ClientService/ClientAddressService, CustomerGroupAddressFeature)
  editCustomerGroup: 'Grupo do Cliente Editado',
  createClientAddress: 'Endereço Cadastrado',
  updateClientAddress: 'Endereço Editado',
  setDefaultClientAddress: 'Endereço Padrão Alterado',
  deactivateClientAddress: 'Endereço Desativado',
  editBillingAddress: 'Endereço do Cadastro Atualizado',
} as const;

type KnownActionType = keyof typeof ACTION_LABELS;
type ActionType = KnownActionType | 'OTHER';

const OTHER_ACTION: ActionType = 'OTHER';

const CREATED_ACTIONS: ActionType[] = [
  'add', 'activate', 'create_scheduled_item', 'create_scheduled_order',
  'create_client_adjustment', 'create_client_adjustment_bulk',
  'create_simulated_shipment', 'confirm_scheduled_order', 'createClientAddress',
];

const EDITED_ACTIONS: ActionType[] = [
  'edit', 'update_scheduled_item', 'update_scheduled_order', 'accumulate_scheduled_order',
  'update_confirmed_scheduled_order', 'update_client_adjustment',
  'update_client_adjustment_bulk', 'update_simulated_shipment',
  'adjustment_change', 'editFee', 'cycle_generation', 'editPaymentPlan', 'editCreateZendeskTicketOnOrder',
];

const REMOVED_ACTIONS: ActionType[] = ['remove', 'delete_client_adjustment',
  'editCustomerGroup', 'updateClientAddress', 'setDefaultClientAddress', 'editBillingAddress',
];

// Agrupamento das actions por categoria de negócio para o dropdown de filtro
// — os cards de atividade continuam mostrando o label granular de
// ACTION_LABELS. Uma categoria é de dois tipos:
// - CRUD: cruza a categoria com Adicionado/Editado/Removido (via
//   CREATED_/EDITED_/REMOVED_ACTIONS), só exibindo a combinação quando ela
//   tem pelo menos uma action real (ex.: "Programado" não tem "Removido").
// - subGroups: opções explícitas definidas à mão, uma por subGroup, todas na
//   MESMA seção do dropdown (ex.: "Frete" tem uma opção só para a taxa do
//   cliente e outra só para envios simulados, sem virar duas seções).
type SubGroup = { key: string; label: string; actions: KnownActionType[] };
type CrudActionGroup = { type: 'crud'; key: string; label: string; actions: KnownActionType[] };
type CustomActionGroup = { type: 'custom'; label: string; subGroups: SubGroup[] };
const ACTION_GROUPS: (CrudActionGroup | CustomActionGroup)[] = [
  { type: 'crud', key: 'produto', label: 'Produto', actions: ['add', 'edit', 'activate', 'remove', 'cycle_generation'] },
  {
    type: 'crud', key: 'programado', label: 'Programado', actions: [
      'create_scheduled_item', 'update_scheduled_item',
      'create_scheduled_order', 'update_scheduled_order', 'accumulate_scheduled_order',
      'update_confirmed_scheduled_order', 'confirm_scheduled_order',
    ],
  },
  {
    type: 'custom', label: 'Reajuste',
    subGroups: [{
      key: 'reajuste', label: 'Reajuste Editado', actions: [
        'adjustment_change', 'create_client_adjustment', 'update_client_adjustment',
        'delete_client_adjustment', 'create_client_adjustment_bulk', 'update_client_adjustment_bulk',
      ],
    }],
  },
  {
    type: 'custom', label: 'Frete',
    subGroups: [
      { key: 'frete', label: 'Frete Editado', actions: ['editFee'] },
      { key: 'simulado', label: 'Simulado Editado', actions: ['create_simulated_shipment', 'update_simulated_shipment'] },
    ],
  },
  {
    type: 'custom', label: 'Pedido',
    subGroups: [
      { key: 'pedido_cancelado', label: 'Pedido Cancelado', actions: ['cancel_order'] },
    ],
  },
  {
    type: 'custom', label: 'Plano de Pagamentos',
    subGroups: [
      { key: 'pagamento', label: 'Plano de Pagamentos Editado', actions: ['editPaymentPlan'] },
    ],
  },
  {
    type: 'custom', label: 'Grupo do Cliente',
    subGroups: [
      { key: 'grupo_cliente', label: 'Grupo do Cliente Editado', actions: ['editCustomerGroup'] },
    ],
  },
  {
    // Endereço é desativado, nunca removido: por isso não usa o CRUD (que rotularia como "Removido").
    type: 'custom', label: 'Endereço',
    subGroups: [
      { key: 'endereco_cadastrado', label: 'Endereço Cadastrado', actions: ['createClientAddress'] },
      {
        key: 'endereco_editado', label: 'Endereço Editado',
        actions: ['updateClientAddress', 'setDefaultClientAddress', 'editBillingAddress'],
      },
      { key: 'endereco_desativado', label: 'Endereço Desativado', actions: ['deactivateClientAddress'] },
    ],
  },
  {
    type: 'custom', label: 'Chamado no Zendesk',
    subGroups: [
      { key: 'chamado_zendesk', label: 'Chamado ao Criar Pedido Editado', actions: ['editCreateZendeskTicketOnOrder'] },
    ],
  },
];

type CrudOp = 'create' | 'update' | 'delete';
const CRUD_LABELS: Record<CrudOp, string> = { create: 'Adicionado', update: 'Editado', delete: 'Removido' };
const CRUD_ACTIONS: Record<CrudOp, ActionType[]> = { create: CREATED_ACTIONS, update: EDITED_ACTIONS, delete: REMOVED_ACTIONS };

const FILTER_GROUPS = ACTION_GROUPS.map((group) => {
  if (group.type === 'custom') {
    return {
      label: group.label,
      options: group.subGroups.map((sub) => ({ value: sub.key, label: sub.label, actions: sub.actions })),
    };
  }

  const options = (['create', 'update', 'delete'] as CrudOp[])
    .map((crud) => ({
      value: `${group.key}:${crud}`,
      label: `${group.label} ${CRUD_LABELS[crud]}`,
      actions: group.actions.filter((action) => CRUD_ACTIONS[crud].includes(action)),
    }))
    .filter((option) => option.actions.length > 0);

  return { label: group.label, options };
}).filter((group) => group.options.length > 0);

const FILTER_OPTION_ACTIONS = new Map<string, KnownActionType[]>(
  FILTER_GROUPS.flatMap((group) => group.options.map((option) => [option.value, option.actions] as const))
);

interface ClientLog extends ProfileHistory {
  action_type: ActionType;
}

interface ClientHistoryProps {
  clientCode: number;
  clientName: string;
}

const PAGE_SIZE = 20;

export function ClientHistory({ clientCode, clientName }: ClientHistoryProps) {
  const [activities, setActivities] = useState<ClientLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (clientCode) {
      fetchActivities();
    } else {
      setActivities([]);
      setLoading(false);
    }
  }, [clientCode]);

  // Reinicia a paginação sempre que os filtros ou os dados carregados mudam
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchTerm, actionFilter, activities]);

  // Itens com comment: null não são eventos de histórico (produtos do perfil
  // sem nenhuma ação registrada ainda) e são descartados aqui. comment.details
  // é uma string JSON solta, sem schema garantido pelo backend — o parse é
  // resiliente a JSON inválido/ausente e a `action` fora do catálogo conhecido.
  const parseAction = (details: string | undefined | null): ActionType => {
    try {
      const parsed = details ? JSON.parse(details) : null;
      const rawAction: string = parsed?.action || '';
      return rawAction in ACTION_LABELS ? (rawAction as KnownActionType) : OTHER_ACTION;
    } catch {
      return OTHER_ACTION;
    }
  };

  const clientLog = (value: ProfileHistory[]): ClientLog[] => {
    return value
      .filter((p): p is ProfileHistory & { comment: NonNullable<ProfileHistory["comment"]> } => !!p?.comment)
      .map((p): ClientLog => ({
        ...p,
        action_type: parseAction(p.comment.details),
      }));
  };
  const fetchActivities = async () => {
    setLoading(true);
    try {
      const { data } = await profileClientApi.getPrfileHistory(Number(clientCode));

      data.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      const tt = clientLog(data);
      setActivities(tt || []);
    } catch (err: any) {
      console.error('❌ Erro ao carregar histórico:', err.message);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType: ActionType) => {
    if (actionType === 'cancel_order') {
      return <XCircle className="h-4 w-4 text-red-600" />;
    }
    if (actionType === 'remove' || actionType === 'delete_client_adjustment') {
      return <Trash2 className="h-4 w-4 text-red-600" />;
    }
    if (actionType === 'deactivateClientAddress') {
      return <PowerOff className="h-4 w-4 text-red-600" />;
    }
    if (actionType === 'editFee' || actionType === 'update_simulated_shipment' || actionType === 'create_simulated_shipment') {
      return <TruckIcon className="h-4 w-4 text-blue-900" />;
    }
    if (CREATED_ACTIONS.includes(actionType)) {
      return <Plus className="h-4 w-4 text-green-600" />;
    }
    if (EDITED_ACTIONS.includes(actionType)) {
      return <Edit className="h-4 w-4 text-blue-600" />;
    }
    return <Activity className="h-4 w-4 text-gray-600" />;
  };

  const getActionLabel = (actionType: ActionType) => {
    if (actionType === 'OTHER') return 'Outros';
    return ACTION_LABELS[actionType];
  };

  const getActionBadgeVariant = (actionType: ActionType): "success" | "default" | "secondary" | "destructive" | "outline" => {
    if (actionType === 'remove' || actionType === 'delete_client_adjustment' || actionType === 'cancel_order'
      || actionType === 'deactivateClientAddress') {
      return 'destructive';
    }
    if (actionType === 'editFee' || actionType === 'update_simulated_shipment' || actionType === 'create_simulated_shipment') {
      return 'outline';
    }
    if (CREATED_ACTIONS.includes(actionType)) {
      return 'success';
    }
    if (EDITED_ACTIONS.includes(actionType)) {
      return 'secondary';
    }
    return 'default';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);

    if (isToday(date)) {
      return `Hoje às ${format(date, 'HH:mm', { locale: ptBR })}`;
    } else if (isYesterday(date)) {
      return `Ontem às ${format(date, 'HH:mm', { locale: ptBR })}`;
    } else if (isWithinInterval(date, { start: startOfWeek(new Date(), { weekStartsOn: 1 }), end: new Date() })) {
      return format(date, "EEEE 'às' HH:mm", { locale: ptBR });
    } else {
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    }
  };

  const toggleExpanded = (activityId: string) => setExpandedItems(prev => ({ ...prev, [activityId]: !prev[activityId] }));

  const filteredActivities = activities.filter((activity) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      activity.productDescription.toLowerCase().includes(searchLower) ||
      activity.productCode.toLowerCase().includes(searchLower) ||
      (activity?.comment?.content && activity?.comment?.content.toLowerCase().includes(searchLower));

    const matchesAction = actionFilter === "all"
      || (actionFilter === "OTHER"
        ? activity.action_type === "OTHER"
        : (FILTER_OPTION_ACTIONS.get(actionFilter) ?? []).includes(activity.action_type as KnownActionType));

    return matchesSearch && matchesAction;
  });

  const visibleActivities = filteredActivities.slice(0, visibleCount);
  const hasMore = visibleCount < filteredActivities.length;

  // Controla o carregamento incremental do render: observa o sentinel no fim da
  // lista e libera mais itens conforme o usuário rola, evitando montar todos os
  // cards de uma vez quando há muitos registros no histórico.
  useEffect(() => {
    if (!hasMore) return;

    const rootEl = scrollContainerRef.current;
    const targetEl = loadMoreRef.current;
    if (!rootEl || !targetEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredActivities.length));
        }
      },
      { root: rootEl, rootMargin: "150px" }
    );

    observer.observe(targetEl);
    return () => observer.disconnect();
  }, [hasMore, filteredActivities.length]);

  const getActivityCounts = () => {
    return {
      total: activities.length,
      added: activities.filter(a => CREATED_ACTIONS.includes(a.action_type)).length,
      edited: activities.filter(a => EDITED_ACTIONS.includes(a.action_type)).length,
      removed: activities.filter(a => REMOVED_ACTIONS.includes(a.action_type)).length,
    };
  };

  const counts = getActivityCounts();

  const formatJsonDetails = (details: any) => {
    if (typeof details === 'string') {
      try {
        return JSON.stringify(JSON.parse(details), null, 2);
      } catch (e) {
        return details; // Return original string if not valid JSON
      }
    }
    return JSON.stringify(details, null, 2);
  };

  if (loading) {
    return (
      <div className="space-y-4 mt-6">
        <div className="animate-pulse">
          <div className="h-24 bg-gray-200 rounded-lg mb-4"></div>
          <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-6">
      {/* Header com estatísticas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-primary" />
            Histórico de Atividades - {clientName}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-primary">{counts.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{counts.added}</div>
              <div className="text-sm text-muted-foreground">Adicionados</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{counts.edited}</div>
              <div className="text-sm text-muted-foreground">Editados</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{counts.removed}</div>
              <div className="text-sm text-muted-foreground">Removidos</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por produto, código, comentário ou usuário..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filtrar por ação" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as ações</SelectItem>
            {FILTER_GROUPS.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectGroup>
            ))}
            <SelectItem value="OTHER">Outros</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de atividades */}
      <div ref={scrollContainerRef} className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
        {filteredActivities.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                {searchTerm || actionFilter !== "all"
                  ? "Nenhuma atividade encontrada com os filtros aplicados."
                  : "Nenhuma atividade registrada para este cliente ainda."}
              </p>
            </CardContent>
          </Card>
        ) : (
          visibleActivities.map((activity, index) => {
            const isExpanded = !!expandedItems[`${activity.productId}-${index}`];
            return (
              <Card key={`${activity.productId}-${index}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 rounded-lg bg-muted/50 mt-1">
                        {getActionIcon(activity.action_type)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h4 className="font-medium text-sm truncate">
                            {activity.productDescription}
                          </h4>
                          <Badge variant={getActionBadgeVariant(activity.action_type)} className="text-xs">
                            {getActionLabel(activity.action_type)}
                          </Badge>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mb-2">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(activity.createdAt)}
                          </div>
                          {activity?.seriesName && (
                            <div className="flex items-center gap-1">
                              <Badge variant={"outline"} className="text-xs">
                                {activity?.seriesName}
                              </Badge>
                            </div>
                          )}
                          {activity?.materialUseName && (
                            <div className="flex items-center gap-1">
                              <Badge variant={"secondary"} className="text-xs">
                                {activity?.materialUseName}
                              </Badge>
                            </div>
                          )}
                          {activity.comment?.author && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {activity.comment.author}
                            </div>
                          )}
                        </div>

                        {activity?.comment?.content && (
                          <div className="flex items-start gap-1 text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                            <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{activity?.comment?.content}</span>
                          </div>
                        )}

                        {isExpanded && activity?.comment?.details && (
                          <div className="mt-3 p-3 bg-muted/20 rounded border-l-2 border-primary/20">
                            <h5 className="font-medium text-xs mb-2">Detalhes:</h5>
                            <pre className="text-xs text-muted-foreground overflow-x-auto">
                              {formatJsonDetails(activity.comment.details)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-2">
                      {activity?.comment?.details && Object.keys(activity?.comment?.details).length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(`${activity.productId}-${index}`)}
                          className="h-8 w-8 p-0"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {hasMore && (
          <div ref={loadMoreRef} className="flex items-center justify-center py-4 text-xs text-muted-foreground">
            Carregando mais atividades...
          </div>
        )}
      </div>
    </div>
  );
}
